import type pino from 'pino';

import { serializeArgs, withMessageFirst } from './serialization.js';

/**
 * Wrapper that provides static-style `debug / info / warn / error` methods
 * on top of a pino instance. Used by the web app for backward compatibility
 * with `Logger.debug(message, ...args)` call sites.
 */
export class StaticLogger {
  private readonly logger: pino.Logger;

  constructor(logger: pino.Logger) {
    this.logger = logger;
  }

  debug(message: string, ...args: unknown[]): void {
    const serialized = serializeArgs(...args);
    if (serialized && Object.keys(serialized).length > 0) {
      this.logger.debug(withMessageFirst(message, serialized));
    } else {
      this.logger.debug(message);
    }
  }

  info(message: string, ...args: unknown[]): void {
    const serialized = serializeArgs(...args);
    if (serialized && Object.keys(serialized).length > 0) {
      this.logger.info(withMessageFirst(message, serialized));
    } else {
      this.logger.info(message);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    const serialized = serializeArgs(...args);
    if (serialized && Object.keys(serialized).length > 0) {
      this.logger.warn(withMessageFirst(message, serialized));
    } else {
      this.logger.warn(message);
    }
  }

  error(message: string, ...args: unknown[]): void {
    const serialized = serializeArgs(...args);

    if (serialized && Object.keys(serialized).length > 0) {
      const hasValidData = Object.values(serialized).some(value => {
        if (value === undefined || value === null) return false;
        if (typeof value === 'object' && value !== null) {
          const nestedValues = Object.values(value as Record<string, unknown>);
          if (nestedValues.length === 0) return false;
          return nestedValues.some(
            nestedValue => nestedValue !== undefined && nestedValue !== null && nestedValue !== ''
          );
        }
        return true;
      });

      if (hasValidData) {
        try {
          // One record, message folded in — the same shape `debug`/`info`/`warn`
          // use. Passing the message as a separate leading argument made node
          // pino read it as the log message and the record as printf
          // interpolation parameters; with no `%o` in the message the record was
          // discarded, so a handled error logged its message and nothing else
          // (TX-4937). Browser pino hands the single object to console.error,
          // where the merged `msg` keeps it readable rather than rendering "{}".
          this.logger.error(withMessageFirst(message, serialized));
        } catch (logError) {
          console.error(
            "[logger] Failed to log error:",
            String(message),
            logError instanceof Error ? logError.message : String(logError)
          );
        }
      } else {
        this.logErrorWithFallback(message, args);
      }
    } else if (args.length > 0) {
      this.logErrorWithFallback(message, args);
    } else {
      this.safeError(message);
    }
  }

  /** Extracts error info from raw args and logs with a fallback strategy. */
  private logErrorWithFallback(message: string, args: unknown[]): void {
    const errorDetails: Record<string, unknown> = {};

    args.forEach((arg, index) => {
      if (arg instanceof Error) {
        errorDetails[`error${index}`] = {
          name: arg.name || 'Error',
          message: arg.message || 'Unknown error',
          stack: arg.stack,
        };
      } else if (arg !== null && arg !== undefined) {
        errorDetails[`arg${index}`] = arg;
      }
    });

    if (Object.keys(errorDetails).length > 0) {
      try {
        this.logger.error(withMessageFirst(message, errorDetails));
      } catch (logError) {
        console.error(
          "[logger] Failed to log error:",
          String(message),
          logError instanceof Error ? logError.message : String(logError)
        );
      }
    } else {
      this.safeError(message);
    }
  }

  private safeError(message: string): void {
    try {
      this.logger.error(message);
    } catch (logError) {
      console.error("[logger] Failed to log:", String(message), logError);
    }
  }
}
